import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { queryClient } from './config/queryClient';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { useTheme } from './contexts/ThemeContext';
import { getTheme } from './theme/themes';
import { CompanyProvider } from './contexts/CompanyContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { AppModeProvider } from './contexts/AppModeContext';
import { WeatherProvider } from './contexts/WeatherContext';
import Layout from './components/Layout/Layout';
import { ConfirmProvider } from './components/ConfirmDialog/ConfirmDialog';
import HomepageRedirect from './components/HomepageRedirect';
import LoadingFallback from './components/Layout/LoadingFallback';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RoleProtectedRoute from './components/Auth/RoleProtectedRoute';
import ComponentProtectedRoute from './components/Auth/ComponentProtectedRoute';
import Login from './pages/Auth/Login';
import SessionHeartbeat from './components/Auth/SessionHeartbeat';
import WeatherBackground from './components/Weather/WeatherBackground';


const Companies = React.lazy(() => import('./pages/Companies/Companies'));
const Users = React.lazy(() => import('./pages/Users/Users'));
const Clients = React.lazy(() => import('./pages/Clients/Clients'));
const ClientDetail = React.lazy(() => import('./pages/Clients/ClientDetail'));
const SalesAssistant = React.lazy(() => import('./pages/SalesAssistant/SalesAssistant'));
const Projects = React.lazy(() => import('./pages/Projects/Projects'));
const ProjectDetail = React.lazy(() => import('./pages/Projects/ProjectDetail'));
const ProjectEdit = React.lazy(() => import('./pages/Projects/ProjectEdit'));
const ProjectCreate = React.lazy(() => import('./pages/Projects/ProjectCreate'));
const Tasks = React.lazy(() => import('./pages/Tasks/Tasks'));
const TaskDetail = React.lazy(() => import('./pages/Tasks/TaskDetail'));
const TaskCreate = React.lazy(() => import('./pages/Tasks/TaskCreate'));
const Tickets = React.lazy(() => import('./pages/Tickets/Tickets'));
const TicketCreate = React.lazy(() => import('./pages/Tickets/TicketCreate'));
const TicketDetail = React.lazy(() => import('./pages/Tickets/TicketDetail'));
const KnowledgeBase = React.lazy(() => import('./pages/KnowledgeBase/KnowledgeBase'));
const ArticleCreate = React.lazy(() => import('./pages/KnowledgeBase/ArticleCreate'));
const ArticleView = React.lazy(() => import('./pages/KnowledgeBase/ArticleView'));
const ArticleEdit = React.lazy(() => import('./pages/KnowledgeBase/ArticleEdit'));
const Documents = React.lazy(() => import('./pages/Documents/Documents'));
const AdminReports = React.lazy(() => import('./pages/Admin/AdminReports'));
const UserActivityReport = React.lazy(() => import('./pages/Reports/UserActivityReport'));
const PatientPortalAccessReport = React.lazy(() => import('./pages/Reports/PatientPortalAccessReport'));
const Profile = React.lazy(() => import('./pages/Profile/Profile'));
const MedicalTests = React.lazy(() => import('./pages/MedicalTests/MedicalTests'));
const MedicalTestDetail = React.lazy(() => import('./pages/MedicalTests/MedicalTestDetail'));
const UserCreate = React.lazy(() => import('./pages/Users/UserCreate'));
const UserDetail = React.lazy(() => import('./pages/Users/UserDetail'));
const ExternalUsers = React.lazy(() => import('./pages/ExternalUsers/ExternalUsers'));
const CompanyCreate = React.lazy(() => import('./pages/Companies/CompanyCreate'));
const CompanyDetail = React.lazy(() => import('./pages/Companies/CompanyDetail'));
const LISPortal = React.lazy(() => import('./pages/LIS/LISPortal'));
const LisConfigurations = React.lazy(() => import('./pages/LIS/LisConfigurations'));
const LisQueryConfigurations = React.lazy(() => import('./pages/LIS/LisQueryConfigurations'));
const LisLocator = React.lazy(() => import('./pages/LIS/LisLocator'));
const OrderManagement = React.lazy(() => import('./pages/LIS/OrderManagement'));
const Billing = React.lazy(() => import('./pages/Billing/Billing'));
const DailyMonitoring = React.lazy(() => import('./pages/Billing/DailyMonitoring'));
const BillingOrderDetail = React.lazy(() => import('./pages/Billing/BillingOrderDetail'));
const BillingPatients = React.lazy(() => import('./pages/Billing/BillingPatients'));
const BillingClaims = React.lazy(() => import('./pages/Billing/BillingClaims'));
const BillingPatientDetail = React.lazy(() => import('./pages/Billing/BillingPatientDetail'));
const VOBList = React.lazy(() => import('./pages/Billing/VOB/VOBList'));
const ProjectTemplates = React.lazy(() => import('./pages/ProjectTemplates/ProjectTemplates'));
const TaskTemplates = React.lazy(() => import('./pages/TaskTemplates/TaskTemplates'));
const ProjectTemplateCreatePage = React.lazy(() => import('./pages/ProjectTemplates/ProjectTemplateCreatePage'));
const ProjectTemplateEditPage = React.lazy(() => import('./pages/ProjectTemplates/ProjectTemplateEditPage'));
const Analytics = React.lazy(() => import('./pages/Analytics/Analytics'));
const GoogleWorkspaceManagement = React.lazy(() => import('./pages/GoogleWorkspace/GoogleWorkspaceManagement'));
const MerakiVPNManagement = React.lazy(() => import('./pages/Meraki/MerakiVPNManagement'));
const AccessGovernance = React.lazy(() => import('./pages/Admin/AccessGovernance'));
const MyAssignments = React.lazy(() => import('./pages/MyAssignments/MyAssignments'));
const MyEmployeesAssignments = React.lazy(() => import('./pages/MyEmployeesAssignments/MyEmployeesAssignments'));
const Calendar = React.lazy(() => import('./pages/Calendar/Calendar'));
const AuditLogs = React.lazy(() => import('./pages/AuditLogs/AuditLogs'));
const CompanyAdminFunctions = React.lazy(() => import('./pages/CompanyAdminFunctions/CompanyAdminFunctions'));
const Departments = React.lazy(() => import('./pages/Departments/Departments'));
const CompanyStructure = React.lazy(() => import('./pages/Organization/CompanyStructure'));
const FailedHpvFiles = React.lazy(() => import('./pages/Tools/FailedHpvFiles'));
const S3UploadActivity = React.lazy(() => import('./pages/Tools/S3UploadActivity'));
const PgxPipeline = React.lazy(() => import('./pages/Integrations/PgxPipeline'));
const CompletedOrders = React.lazy(() => import('./pages/Integrations/CompletedOrders'));
const CollaborateMdConsole = React.lazy(() => import('./pages/Integrations/CollaborateMdConsole'));
const EmailWebhookEventsReport = React.lazy(() => import('./pages/Tools/EmailWebhookEventsReport'));
const IntegrationsDashboard = React.lazy(() => import('./pages/Integrations/IntegrationsDashboard'));
const ToolsDashboard = React.lazy(() => import('./pages/Tools/ToolsDashboard'));
const S3Browser = React.lazy(() => import('./pages/Tools/S3Browser'));
const DbConsole = React.lazy(() => import('./pages/Tools/DbConsole'));
const AiPromptConsole = React.lazy(() => import('./pages/Tools/AiPromptConsole'));
const DbPerformance = React.lazy(() => import('./pages/Tools/DbPerformance'));

// Inventory V1
const InventoryDashboard = React.lazy(() => import('./pages/Inventory/Dashboard'));
const InventoryItems = React.lazy(() => import('./pages/Inventory/InventoryItems'));
const InventoryItemEdit = React.lazy(() => import('./pages/Inventory/InventoryItemEdit'));
const InventoryLocations = React.lazy(() => import('./pages/Inventory/Locations'));
const InventoryDepartments = React.lazy(() => import('./pages/Inventory/Departments'));
const InventoryStockLevels = React.lazy(() => import('./pages/Inventory/StockLevels'));
const InventoryShipments = React.lazy(() => import('./pages/Inventory/Shipments'));
const InventoryReceiving = React.lazy(() => import('./pages/Inventory/Receiving'));
const InventoryTransactionLog = React.lazy(() => import('./pages/Inventory/TransactionLog'));
const OrderRequests = React.lazy(() => import('./pages/Inventory/OrderRequests'));
const PurchaseOrdersPage = React.lazy(() => import('./pages/Inventory/PurchaseOrders'));

const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const Config = React.lazy(() => import('./pages/Config/Config'));
const CronJobsManagement = React.lazy(() => import('./pages/CronJobs/CronJobsManagement'));
const EscalationRulesAdmin = React.lazy(() => import('./pages/Admin/EscalationRulesAdmin'));
const UserComponentAccess = React.lazy(() => import('./pages/Admin/UserAccess/UserComponentAccess'));
const CategoryManagement = React.lazy(() => import('./pages/Admin/CategoryManagement'));

const EmailRouting = React.lazy(() => import('./pages/EmailIntegration/EmailRouting'));
const WebhookConfiguration = React.lazy(() => import('./pages/EmailIntegration/WebhookConfiguration'));
const NotificationSettings = React.lazy(() => import('./pages/Settings/NotificationSettings'));
const PortalRequest = React.lazy(() => import('./pages/PortalRequest/PortalRequest'));
const Support = React.lazy(() => import('./pages/Public/Support'));

const LISOrdersReport = React.lazy(() => import('./pages/Admin/LISOrdersReport'));
const AccessAuditReport = React.lazy(() => import('./pages/Reports/AccessAuditReport'));
const ProjectStatusReport = React.lazy(() => import('./pages/Reports/ProjectStatusReport'));
const EmployeeTeamReport = React.lazy(() => import('./pages/Reports/EmployeeTeamReport'));
const TicketReport = React.lazy(() => import('./pages/Reports/TicketReport'));
const CompanyHierarchyReport = React.lazy(() => import('./pages/Reports/CompanyHierarchyReport'));
const DailyActivityReport = React.lazy(() => import('./pages/Reports/DailyActivityReport'));
const TaskCompletionReport = React.lazy(() => import('./pages/Reports/TaskCompletionReport'));
const LisTestTypesByClientReport = React.lazy(() => import('./pages/Reports/LisTestTypesByClientReport'));
const SalesPerformanceReport = React.lazy(() => import('./pages/Reports/SalesPerformanceReport'));
const AsyncEventManagement = React.lazy(() => import('./pages/Admin/AsyncEventManagement'));
const AiInteractionLog = React.lazy(() => import('./pages/Admin/AiInteractionLog'));


const AppContent: React.FC = () => {
  const { themeMode } = useTheme();
  const theme = getTheme(themeMode);

  return (
    <ThemeProvider theme={theme}>
      <SessionHeartbeat />
      <WeatherBackground />
      <ConfirmProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>

        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/support" element={
            <Suspense fallback={<LoadingFallback />}>
              <Support />
            </Suspense>
          } />
          <Route path="/knowledge-base" element={
            <Suspense fallback={<LoadingFallback />}>
              <KnowledgeBase />
            </Suspense>
          } />
          <Route path="/knowledge-base/new" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ArticleCreate />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/knowledge-base/:id/edit" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ArticleEdit />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/knowledge-base/:id" element={
            <Suspense fallback={<LoadingFallback />}>
              <ArticleView />
            </Suspense>
          } />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<HomepageRedirect />} />
                      <Route path="/my-assignments" element={<MyAssignments />} />
                      <Route path="/my-employees-assignments" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <MyEmployeesAssignments />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/calendar" element={<Calendar />} />
                      <Route path="/companies" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <Companies />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/companies/new" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <CompanyCreate />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/companies/:id" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <CompanyDetail />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/users" element={<Users />} />
                      <Route path="/users/new" element={<UserCreate />} />
                      <Route path="/external-users" element={<ExternalUsers />} />
                      <Route path="/users/:id" element={<UserDetail />} />
                      {/* Open to any authenticated user (departments are an org-wide
                          roster; the API + nav are already open). Create/edit/delete
                          stay gated by crm:manage inside the page. */}
                      <Route path="/departments" element={<Departments />} />
                      <Route path="/organization/structure" element={
                        <RoleProtectedRoute allowedRoles={['system_admin', 'employee']}>
                          <CompanyStructure />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/clients" element={<Clients />} />
                      <Route path="/clients/:id" element={<ClientDetail />} />
                      {/* AI Assistant. CRM-level gate (incl. restricted salespeople + sysadmin override) is
                          enforced by /api/sales-assistant; the page renders an empty state otherwise.
                          The old /sales-assistant URL keeps working. */}
                      <Route path="/assistant" element={<SalesAssistant />} />
                      <Route path="/sales-assistant" element={<Navigate to="/assistant" replace />} />
                      <Route path="/projects" element={<Projects />} />
                      <Route path="/projects/new" element={<ProjectCreate />} />
                      <Route path="/projects/:id" element={<ProjectDetail />} />
                      <Route path="/projects/:id/edit" element={<ProjectEdit />} />
                      <Route path="/project-templates" element={
                        <ComponentProtectedRoute componentName="crm" requiredAccess="manage">
                          <ProjectTemplates />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/project-templates/new" element={
                        <ComponentProtectedRoute componentName="crm" requiredAccess="manage">
                          <ProjectTemplateCreatePage />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/project-templates/:id" element={
                        <ComponentProtectedRoute componentName="crm" requiredAccess="manage">
                          <ProjectTemplateEditPage />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/task-templates" element={<TaskTemplates />} />
                      <Route path="/tasks" element={<Tasks />} />
                      <Route path="/tasks/new" element={<TaskCreate />} />
                      <Route path="/tasks/:id" element={<TaskDetail />} />
                      <Route path="/tasks/:id/edit" element={<TaskDetail />} />
                      <Route path="/tickets" element={<Tickets />} />
                      <Route path="/tickets/new" element={<TicketCreate />} />
                      <Route path="/tickets/:id" element={<TicketDetail />} />
                      <Route path="/documents" element={<Documents />} />

                      {/* Inventory Routes */}
                      <Route path="/inventory" element={
                        <ComponentProtectedRoute componentName="inventory-dashboard">
                          <InventoryDashboard />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/items" element={
                        <ComponentProtectedRoute componentName="inventory-items">
                          <InventoryItems />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/items/new" element={
                        <ComponentProtectedRoute componentName="inventory-items">
                          <InventoryItemEdit />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/items/:id/edit" element={
                        <ComponentProtectedRoute componentName="inventory-items">
                          <InventoryItemEdit />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/locations" element={
                        <ComponentProtectedRoute componentName="inventory-locations">
                          <InventoryLocations />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/departments" element={
                        <ComponentProtectedRoute componentName="inventory-items">
                          <InventoryDepartments />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/stock" element={
                        <ComponentProtectedRoute componentName="inventory-stock">
                          <InventoryStockLevels />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/shipments" element={
                        <ComponentProtectedRoute componentName="inventory-shipments">
                          <InventoryShipments />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/receiving" element={
                        <ComponentProtectedRoute componentName="inventory-receiving">
                          <InventoryReceiving />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/transactions" element={
                        <ComponentProtectedRoute componentName="inventory-transactions">
                          <InventoryTransactionLog />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/order-requests" element={
                        <ComponentProtectedRoute componentName="inventory-order-requests" allowCrmRestricted>
                          <OrderRequests />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/inventory/purchase-orders" element={
                        <ComponentProtectedRoute componentName="inventory-purchase-orders">
                          <PurchaseOrdersPage />
                        </ComponentProtectedRoute>
                      } />

                      {/* /reports → redirect to first report */}
                      <Route path="/reports" element={<Navigate to="/reports/access-audit" replace />} />

                      {/* Individual report pages — each gated by its own component access key */}

                      <Route path="/reports/lis-orders-by-status" element={
                        <ComponentProtectedRoute componentName={['report-lis-orders-by-status', 'crm']} requiredAccess="view">
                          <LISOrdersReport />
                        </ComponentProtectedRoute>
                      } />
                      {/* LIS Test Types by Client — report grant OR CRM; restricted
                          salespeople run it for their assigned clients only (server-enforced). */}
                      <Route path="/reports/lis-test-types-by-client" element={
                        <ComponentProtectedRoute componentName={['report-lis-test-types-by-client', 'crm']} requiredAccess="view" allowCrmRestricted>
                          <LisTestTypesByClientReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/access-audit" element={
                        <ComponentProtectedRoute componentName="report-access-audit" requiredAccess="view">
                          <AccessAuditReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/projects" element={
                        <ComponentProtectedRoute componentName="report-projects" requiredAccess="view">
                          <ProjectStatusReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/employee" element={
                        <ComponentProtectedRoute componentName="report-employee" requiredAccess="view">
                          <EmployeeTeamReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/tickets" element={
                        <ComponentProtectedRoute componentName="report-tickets" requiredAccess="view">
                          <TicketReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/company-hierarchy" element={
                        <ComponentProtectedRoute componentName="report-company-hierarchy" requiredAccess="view">
                          <CompanyHierarchyReport />
                        </ComponentProtectedRoute>
                      } />

                      <Route path="/reports/user-activity" element={
                        <ComponentProtectedRoute componentName="user-activity-report" requiredAccess="view">
                          <UserActivityReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/patient-portal-access" element={
                        <ComponentProtectedRoute componentName="crm" requiredAccess="manage">
                          <PatientPortalAccessReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/daily-activity" element={
                        <ComponentProtectedRoute componentName="report-daily-activity" requiredAccess="view">
                          <DailyActivityReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/reports/task-completion" element={
                        <ComponentProtectedRoute componentName="report-task-completion" requiredAccess="view">
                          <TaskCompletionReport />
                        </ComponentProtectedRoute>
                      } />
                      {/* Sales Performance — CRM managers/admins (all salespeople) OR
                          CRM 'restricted' salespeople (auto-scoped to themselves). */}
                      <Route path="/reports/sales-performance" element={
                        <ComponentProtectedRoute componentName={['report-sales-performance', 'crm']} requiredAccess="manage" allowCrmRestricted>
                          <SalesPerformanceReport />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/admin/reports" element={
                        <ComponentProtectedRoute componentName="users" requiredAccess="manage">
                          <AdminReports />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/analytics" element={
                        <RoleProtectedRoute allowedRoles={['system_admin', 'employee', 'salesperson']}>
                          <Analytics />
                        </RoleProtectedRoute>
                      } />
                      {/* Integrations portal landing/status hub. Gated by the same grants
                          that surface the portal toggle (system-admin auto-passes). */}
                      <Route path="/integrations" element={
                        <ComponentProtectedRoute componentName={['google-workspace', 'integrations-file-monitor']}>
                          <IntegrationsDashboard />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/google-workspace" element={
                        <ComponentProtectedRoute componentName="google-workspace" requiredAccess="manage">
                          <GoogleWorkspaceManagement />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/meraki-vpn" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <MerakiVPNManagement />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/access-governance" element={
                        <ComponentProtectedRoute componentName="access-governance" requiredAccess="manage">
                          <AccessGovernance />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/config" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <Config />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/cron-jobs" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <CronJobsManagement />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/escalation-rules" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <EscalationRulesAdmin />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/ai-interactions" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <AiInteractionLog />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/async-events" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <AsyncEventManagement />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/medical-tests" element={
                        <RoleProtectedRoute allowedRoles={['system_admin', 'employee']}>
                          <MedicalTests />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/medical-tests/:id" element={
                        <RoleProtectedRoute allowedRoles={['system_admin', 'employee']}>
                          <MedicalTestDetail />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/lis" element={<LISPortal />} />
                      <Route path="/lis/locator" element={<LisLocator />} />
                      <Route path="/lis/orders" element={<OrderManagement />} />
                      <Route path="/lis/configurations" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <LisConfigurations />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/lis/query-configurations" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <LisQueryConfigurations />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/billing/monitoring" element={
                        <ComponentProtectedRoute componentName="billing-daily-monitoring">
                          <DailyMonitoring />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/billing/orders/:id" element={<BillingOrderDetail />} />
                      <Route path="/billing/claims" element={
                        <ComponentProtectedRoute componentName="billing-daily-monitoring">
                          <BillingClaims />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/billing/patients" element={<BillingPatients />} />
                      <Route path="/billing/patients/:patientNo" element={<BillingPatientDetail />} />
                      {/* Locator: same shared page as /lis/locator, surfaced under Billing too. */}
                      <Route path="/billing/locator" element={<LisLocator />} />
                      {/* /billing-portal redirects to /billing for any old bookmarks. */}
                      <Route path="/billing-portal" element={<Navigate to="/billing" replace />} />

                      <Route path="/billing/vob" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <VOBList />
                        </RoleProtectedRoute>
                      } />
                      {/* /vob redirects to /billing/vob so old bookmarks keep working. */}
                      <Route path="/vob" element={<Navigate to="/billing/vob" replace />} />

                      <Route path="/tools/failed-hpv-files" element={
                        <ComponentProtectedRoute componentName="tools-hpv-ish-report-errors">
                          <FailedHpvFiles />
                        </ComponentProtectedRoute>
                      } />

                      <Route path="/tools-hpv-ish-report-errors" element={
                        <ComponentProtectedRoute componentName="tools-hpv-ish-report-errors">
                          <FailedHpvFiles />
                        </ComponentProtectedRoute>
                      } />

                      <Route path="/tools/s3-upload-activity" element={
                        <ComponentProtectedRoute componentName="integrations-file-monitor">
                          <S3UploadActivity />
                        </ComponentProtectedRoute>
                      } />

                      {/* Tools portal (system-admin only) */}
                      <Route path="/tools" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <ToolsDashboard />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/tools/s3-browser" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <S3Browser />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/tools/ai-prompts" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <AiPromptConsole />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/tools/db-console" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <DbConsole />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/tools/db-performance" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <DbPerformance />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/integrations/pgx-pipeline" element={
                        <ComponentProtectedRoute componentName="integrations-pgx-pipeline">
                          <PgxPipeline />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/integrations/completed-orders" element={
                        <ComponentProtectedRoute componentName="integrations-completed-orders">
                          <CompletedOrders />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/integrations/collaboratemd-console" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <CollaborateMdConsole />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/tools/email-webhook-events" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <EmailWebhookEventsReport />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/audit-logs" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <AuditLogs />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/settings" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <Settings />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/settings/notifications" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <NotificationSettings />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/company-admin-functions" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <CompanyAdminFunctions />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/user-component-access" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <UserComponentAccess />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/categories" element={
                        <ComponentProtectedRoute componentName="crm" requiredAccess="manage">
                          <CategoryManagement />
                        </ComponentProtectedRoute>
                      } />
                      <Route path="/email-integration/routing" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <EmailRouting />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/email-integration/webhooks" element={
                        <RoleProtectedRoute allowedRoles={['system_admin']}>
                          <WebhookConfiguration />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/portal-request" element={<PortalRequest />} />

                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Box>
      </ConfirmProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CustomThemeProvider>
        <AppModeProvider>
          <ImpersonationProvider>
            <CompanyProvider>
              <WeatherProvider>
                <AppContent />
              </WeatherProvider>
            </CompanyProvider>
          </ImpersonationProvider>
        </AppModeProvider>
      </CustomThemeProvider>
    </QueryClientProvider>
  );
}

export default App;

